import React, { useEffect, useState, useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Home,
  Grid3x3,
  Calendar,
  Briefcase,
  Newspaper,
  Navigation,
  MapPin,
  Map as MapIcon,
  Menu,
  X,
} from "lucide-react";
import { useSiteSettings, isPageVisible } from "../lib/siteSettings";

const NAV_ITEMS = [
  { key: "home", label: "Strona główna", icon: Home, route: "/" },
  { key: "zones", label: "Strefy", icon: Grid3x3, route: "/strefy" },
  { key: "schedule", label: "Wydarzenia", icon: Calendar, route: "/wydarzenia" },
  { key: "map", label: "Mapa", icon: MapIcon, route: "/mapa" },
  { key: "exhibitors", label: "Wystawcy", icon: Briefcase, route: "/wystawcy" },
  { key: "info", label: "Atrakcje", icon: Newspaper, route: "/atrakcje" },
  { key: "directions", label: "Jak dojadę", icon: Navigation, route: "/jak-dojade" },
];

function useIsMobile() {
  const [m, setM] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 768 : false));
  useEffect(() => {
    const onResize = () => setM(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return m;
}

export default function Sidebar() {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(false);
  const siteSettings = useSiteSettings();

  // Filtrujemy NAV_ITEMS zgodnie z widocznością z site_settings.json.
  // Fail-open — brak flagi = element widoczny. „home" zawsze widoczna.
  const navItems = useMemo(
    () => NAV_ITEMS.filter((it) => it.key === "home" || isPageVisible(siteSettings, it.key)),
    [siteSettings]
  );

  // Close drawer when route changes (mobile)
  useEffect(() => {
    if (isMobile) setExpanded(false);
  }, [location.pathname, isMobile]);

  const drawerWidth = isMobile ? 260 : 230;

  // ─── Mobile: completely hidden sidebar + floating toggle button + slide-in drawer ───
  if (isMobile) {
    return (
      <>
        {/* Floating hamburger toggle - always visible top-left */}
        <button
          onClick={() => setExpanded(true)}
          aria-label="Otwórz menu"
          data-testid="sidebar-toggle"
          className="fixed top-3 left-3 z-30 flex items-center justify-center transition-transform active:scale-95"
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            backgroundColor: "#002D6A",
            color: "#fff",
            boxShadow: "0 4px 12px rgba(0,29,106,0.4)",
            border: "1px solid rgba(0,176,176,0.4)",
            opacity: expanded ? 0 : 1,
            pointerEvents: expanded ? "none" : "auto",
            transition: "opacity 180ms ease",
          }}
        >
          <Menu size={22} />
        </button>

        {/* Backdrop */}
        {expanded && (
          <div
            onClick={() => setExpanded(false)}
            className="fixed inset-0 z-40"
            style={{ backgroundColor: "rgba(0,20,60,0.6)" }}
            data-testid="sidebar-backdrop"
          />
        )}

        {/* Drawer */}
        <aside
          className="fixed top-0 bottom-0 left-0 flex flex-col"
          style={{
            width: drawerWidth,
            zIndex: 50,
            backgroundColor: "#002D6A",
            borderRight: "1px solid rgba(0,176,176,0.2)",
            paddingTop: 20,
            paddingBottom: 16,
            transform: expanded ? "translateX(0)" : "translateX(-100%)",
            transition: "transform 240ms cubic-bezier(0.2, 0.8, 0.2, 1)",
            boxShadow: expanded ? "8px 0 24px rgba(0,0,0,0.3)" : "none",
          }}
          data-testid="desktop-sidebar"
        >
          {/* Header with close button */}
          <div className="flex items-center justify-between px-4 pb-4">
            <NavLink to="/" className="flex items-center gap-[10px] min-w-0" data-testid="sidebar-logo-link">
              <img
                src="/images/logo_home_icon.webp"
                alt="logo"
                width="38"
                height="38"
                style={{ width: 38, height: 38, objectFit: "contain", flexShrink: 0 }}
              />
              <div>
                <div style={{ color: "#fff", fontSize: 15, fontWeight: 800, letterSpacing: 0.5 }}>Łódź You Play</div>
                <div style={{ color: "#FF9900", fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>2026</div>
              </div>
            </NavLink>
            <button
              onClick={() => setExpanded(false)}
              aria-label="Zamknij menu"
              className="flex items-center justify-center hover:bg-white/10 transition-colors"
              style={{ width: 36, height: 36, borderRadius: 9 }}
              data-testid="sidebar-close"
            >
              <X size={22} color="#fff" />
            </button>
          </div>

          <div className="mx-[14px] my-[6px] h-px" style={{ background: "rgba(173,216,230,0.15)" }} />

          <nav className="flex-1 pt-[6px] flex flex-col gap-[1px]">
            {navItems.map((item) => (
              <NavLink
                key={item.key}
                to={item.route}
                end={item.route === "/"}
                data-testid={`sidebar-nav-${item.key}`}
                className={({ isActive }) =>
                  `relative flex items-center mx-2 px-[14px] py-[10px] rounded-[10px] gap-[10px] transition-all duration-200 ${
                    isActive ? "lyp-active" : "hover:bg-white/5"
                  }`
                }
                style={({ isActive }) => ({
                  backgroundColor: isActive ? "rgba(0,176,176,0.15)" : "transparent",
                })}
              >
                {({ isActive }) => (
                  <>
                    <div
                      className="flex items-center justify-center"
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 9,
                        backgroundColor: isActive ? "#0049A8" : "rgba(255,255,255,0.06)",
                        flexShrink: 0,
                      }}
                    >
                      <item.icon size={20} color={isActive ? "#fff" : "#7BA3C7"} />
                    </div>
                    <span
                      style={{
                        color: isActive ? "#fff" : "#7BA3C7",
                        fontSize: 14,
                        fontWeight: isActive ? 700 : 500,
                      }}
                    >
                      {item.label}
                    </span>
                    {isActive && (
                      <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded" style={{ background: "#FF9900" }} />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="mx-[14px] my-[6px] h-px" style={{ background: "rgba(173,216,230,0.15)" }} />
          <div className="px-[18px] flex flex-col gap-1">
            <div className="flex items-center gap-[6px] mt-[6px]">
              <MapPin size={14} color="#00B0B0" />
              <span style={{ color: "#ADD8E6", fontSize: 12, fontWeight: 600 }}>EC1 Łódź</span>
            </div>
            <span style={{ color: "#7BA3C7", fontSize: 11, paddingLeft: 20 }}>18-19 Lipca 2026</span>
          </div>
        </aside>
      </>
    );
  }

  // ─── Desktop: standard inline sidebar (unchanged) ───
  return (
    <aside
      className="flex-shrink-0 flex flex-col"
      style={{
        width: 230,
        backgroundColor: "#002D6A",
        borderRight: "1px solid rgba(0,176,176,0.2)",
        paddingTop: 20,
        paddingBottom: 16,
      }}
      data-testid="desktop-sidebar"
    >
      <NavLink to="/" className="flex items-center gap-[10px]" style={{ paddingLeft: 18, paddingRight: 18, paddingBottom: 16 }} data-testid="sidebar-logo-link">
        <img src="/images/logo_home_icon.webp" alt="logo" width="42" height="42" style={{ width: 42, height: 42, objectFit: "contain" }} />
        <div>
          <div style={{ color: "#fff", fontSize: 16, fontWeight: 800, letterSpacing: 0.5 }}>Łódź You Play</div>
          <div style={{ color: "#FF9900", fontSize: 12, fontWeight: 700, letterSpacing: 1 }}>2026</div>
        </div>
      </NavLink>

      <div className="mx-[14px] my-[6px] h-px" style={{ background: "rgba(173,216,230,0.15)" }} />

      <nav className="flex-1 pt-[6px] flex flex-col gap-[1px]">
        {navItems.map((item) => (
          <NavLink
            key={item.key}
            to={item.route}
            end={item.route === "/"}
            data-testid={`sidebar-nav-${item.key}`}
            className={({ isActive }) =>
              `relative flex items-center mx-2 px-[14px] py-[10px] rounded-[10px] gap-[10px] transition-all duration-200 ${
                isActive ? "lyp-active" : "hover:bg-white/5"
              }`
            }
            style={({ isActive }) => ({
              backgroundColor: isActive ? "rgba(0,176,176,0.15)" : "transparent",
            })}
          >
            {({ isActive }) => (
              <>
                <div
                  className="flex items-center justify-center"
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 9,
                    backgroundColor: isActive ? "#0049A8" : "rgba(255,255,255,0.06)",
                  }}
                >
                  <item.icon size={20} color={isActive ? "#fff" : "#7BA3C7"} />
                </div>
                <span
                  style={{
                    color: isActive ? "#fff" : "#7BA3C7",
                    fontSize: 13,
                    fontWeight: isActive ? 700 : 500,
                  }}
                >
                  {item.label}
                </span>
                {isActive && <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded" style={{ background: "#FF9900" }} />}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="mx-[14px] my-[6px] h-px" style={{ background: "rgba(173,216,230,0.15)" }} />
      <div className="px-[18px] flex flex-col gap-1">
        <div className="flex items-center gap-[6px] mt-[6px]">
          <MapPin size={14} color="#00B0B0" />
          <span style={{ color: "#ADD8E6", fontSize: 12, fontWeight: 600 }}>EC1 Łódź</span>
        </div>
        <span style={{ color: "#7BA3C7", fontSize: 11, paddingLeft: 20 }}>18-19 Lipca 2026</span>
      </div>
    </aside>
  );
}
